import { useEffect, useState, useMemo, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPersonRunning, faPersonBiking, faPersonSwimming, faPersonWalking,
  faBullseye, faBolt, faDumbbell, faTriangleExclamation,
} from "@fortawesome/free-solid-svg-icons";
import api from "../services/api";
import styles from "./CardioCheckinModal.module.css";
import ModalHeader from "./ModalHeader";
import ConfirmDialog from "./ConfirmDialog";
import { useBodyScrollLock } from "../hooks/useBodyScrollLock";
import { getCssVar } from "../utils/theme";
import { useScrollFade } from "../hooks/useScrollFade";
import { getCurrentPositionSafe } from "../utils/geolocation";
import { useConfirmDialog } from "../hooks/useConfirmDialog";
import { generateIdempotencyKey } from "../utils/idempotencyKey";


// Maps exercise name/muscle_group to a FontAwesome icon
function normalize(str) {
  return (str || '').toLowerCase()
    .normalize('NFD').replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9 ]/g, '');
}
function getExerciseIcon(item) {
  const h = normalize((item.name || '') + ' ' + (item.muscle_group || ''));
  if (/bicicleta|bike|cicl|spinning/.test(h))   return faPersonBiking;
  if (/natacao|nadar|swim|aqua|piscina/.test(h)) return faPersonSwimming;
  if (/caminhada|walk|andar/.test(h))            return faPersonWalking;
  if (/core|abdomen|abdomin|prancha|plank/.test(h)) return faBullseye;
  if (/hiit|tabata|circuito|funcional/.test(h))  return faBolt;
  if (/remo|rowing|force|muscula/.test(h))        return faDumbbell;
  return faPersonRunning;
}

const CONFETTI_CSS = `
@keyframes cf-burst-arc {
  0%   {
    transform: translate(-50%, -50%) scale(0.3) rotate(0deg);
    opacity: 0;
    animation-timing-function: cubic-bezier(0.11, 0.85, 0.3, 1);
  }
  5%   { opacity: 1; }
  22%  {
    transform: translate(calc(-50% + var(--cf-bx)), calc(-50% + var(--cf-by))) scale(1) rotate(calc(var(--cf-rot) * 0.3));
    opacity: 1;
    animation-timing-function: cubic-bezier(0.45, 0, 0.82, 0.4);
  }
  88%  { opacity: 0.75; }
  100% {
    transform: translate(calc(-50% + var(--cf-bx) + var(--cf-tx)), calc(-50% + var(--cf-by) + 560px)) rotate(var(--cf-rot));
    opacity: 0;
  }
}
@keyframes cf-shock {
  0%   { transform: translate(-50%, -50%) scale(0.2); opacity: 0.6; }
  55%  { opacity: 0.22; }
  100% { transform: translate(-50%, -50%) scale(2.6); opacity: 0; }
}
`;

const DURATION_OPTIONS = [30, 60, 90];

export default function CardioCheckinModal({ open, onClose, onSuccess, photoRequired }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);
  const [duration, setDuration] = useState(null);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const [location, setLocation] = useState(null);
  const [photo, setPhoto] = useState(null);
  // Aviso de horário fisicamente implausível (409 checkin_overlap) — mesmo
  // padrão do useCheckin.js, mas com diálogo próprio já que este modal não
  // compartilha estado com o fluxo de check-in prescrito.
  const { confirmState, openConfirm, closeConfirm, runConfirm } = useConfirmDialog();

  // Chave de idempotência: uma por abertura do modal, reaproveitada em todo
  // retry dessa tentativa (inclusive a reconfirmação após o aviso de
  // sobreposição) — ver comentário equivalente em hooks/useCheckin.js.
  const idempotencyKeyRef = useRef(null);

  useEffect(() => {
    if (!open) { setSelected(null); setDuration(null); setError(""); setResult(null); setLocation(null); setPhoto(null); idempotencyKeyRef.current = null; return; }
    idempotencyKeyRef.current = generateIdempotencyKey();
    setLoading(true);
    api.get("/exercises/cardio-items")
      .then(r => setItems(r.data))
      .catch(() => setError("Não foi possível carregar os exercícios."))
      .finally(() => setLoading(false));

    // Captura a localização assim que o modal abre (não no submit) — dá ao
    // usuário o tempo que ele precisar pra responder ao prompt nativo de
    // permissão do navegador enquanto ainda está escolhendo o exercício,
    // em vez de competir com o timeout no momento de salvar.
    let cancelled = false;
    getCurrentPositionSafe().then((loc) => { if (!cancelled) setLocation(loc); });
    return () => { cancelled = true; };
  }, [open]);

  useBodyScrollLock(open);
  const { scrollRef, canScrollMore, check } = useScrollFade();
  
  // Confete: partículas geradas na abertura do resultado.
  // Explosão: ângulo restrito ao hemisfério superior (180°-360°) pra estourar
  // pra cima/pros lados, nunca pra baixo — a queda (fase 2 do keyframe) assume depois.
  const particles = useMemo(() => {
    if (!result) return [];
    const shapes = [
      { w: 3,  h: 11, r: "1px"  },
      { w: 3,  h: 11, r: "1px"  },
      { w: 6,  h: 6,  r: "2px"  },
      { w: 5,  h: 5,  r: "50%"  },
    ];
    const colorMap = [0, 0, 0, 1, 1, 1, 2, 2, 3];
    return Array.from({ length: 72 }, (_, i) => {
      const sh = shapes[i % shapes.length];
      const angle = Math.PI + Math.random() * Math.PI; // 180°-360° (leque pra cima)
      const dist  = 55 + Math.random() * 130;           // alcance do estouro
      return {
        bx:       Math.cos(angle) * dist, // deslocamento X da explosão
        by:       Math.sin(angle) * dist, // deslocamento Y da explosão (sempre <= 0)
        w:        sh.w,
        h:        sh.h + Math.random() * 4,
        round:    sh.r,
        colorIdx: colorMap[i % colorMap.length],
        duration: 2.0 + (i % 7) * 0.30,
        delay:    (i % 24) * 0.012, // quase simultâneo — precisa parecer explosão, não gotejar
        tx:       (Math.random() - 0.5) * 150,
        rot:      Math.random() * 660 - 330,
      };
    });
  }, [result]); // Regenera quando result muda
  
  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  
  if (!open) return null;

  async function submitCardioCheckin(confirmOverlap) {
    setSaving(true);
    try {
      const formData = new FormData();
      formData.append("exercise_item_id", selected.id);
      formData.append("duration", duration);
      if (confirmOverlap) formData.append("confirm_overlap", "true");
      formData.append(
        "idempotency_key",
        idempotencyKeyRef.current || (idempotencyKeyRef.current = generateIdempotencyKey())
      );
      if (location) {
        formData.append("checkin_lat", location.lat);
        formData.append("checkin_lon", location.lon);
        if (location.accuracy != null) formData.append("checkin_accuracy", location.accuracy);
      }
      if (photo) formData.append("photo", photo);
      const res = await api.post("/exercises/cardio-checkin", formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      // Passa se a localização foi capturada — o Dashboard usa isso pra saber
      // se vale a pena reforçar o loadData() nos segundos seguintes esperando
      // o rótulo textual do local (resolvido em background no servidor, ver
      // useCheckin.js/confirmCheckin — mesmo padrão aplicado aqui, que antes
      // não existia nesse fluxo de cardio avulso).
      onSuccess?.(!!location);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const isOverlap =
        err?.response?.status === 409 &&
        detail && typeof detail === "object" && detail.code === "checkin_overlap";

      if (isOverlap && !confirmOverlap) {
        // Duração alegada não cabe no tempo real desde o check-in anterior
        // (ver _check_checkin_overlap_or_raise em app/services/user_service.py)
        // — pede confirmação em vez de bloquear.
        openConfirm({
          title: "Confirmar horário do cardio",
          message: detail.message,
          confirmLabel: "Confirmar mesmo assim",
          cancelLabel: "Revisar horário",
          // "success" (não "primary") — é o único variant do ConfirmDialog
          // com a cor de marca (--blue/--accent, #1d9e75) consistente nos
          // dois temas; "primary" tem um azul hardcoded no tema claro que
          // não bate com o branding (ver ConfirmDialog.module.css).
          confirmVariant: "success",
          onConfirm: () => submitCardioCheckin(true),
        });
        return;
      }

      setError(
        typeof detail === "string" ? detail : "Erro ao registrar cardio."
      );
    } finally {
      setSaving(false);
    }
  }

  async function handleSubmit(e) {
    e.preventDefault();
    if (!selected) { setError("Selecione um exercício."); return; }
    if (!duration) { setError("Informe a duração do cardio."); return; }
    if (photoRequired && !photo) { setError("Anexe uma foto: você participa de um desafio que exige registro fotográfico."); return; }
    setError("");
    await submitCardioCheckin(false);
  }

  return (
    <>
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.modal} onClick={e => e.stopPropagation()}>
        <ModalHeader onClose={onClose} />
        <div className={styles.header}>
          <div className={styles.headerLeft}>
            <span className={styles.icon}><FontAwesomeIcon icon={faPersonRunning} /></span>
            <div>
              <h2 className={styles.title}>Registrar Cardio</h2>
              <p className={styles.sub}>Não conta no limite diário de treinos</p>
            </div>
          </div>
        </div>

        {result ? (
          <div className={styles.success} style={{ position: 'relative', overflow: 'hidden' }}>
            {!prefersReducedMotion && (
              <div style={{
                position: "absolute", top: -70, left: 0, right: 0, height: 560,
                overflow: "hidden", pointerEvents: "none", zIndex: 10,
              }}>
                <style>{CONFETTI_CSS}</style>
                {(() => {
                  const accentRgb = getCssVar("--accent-rgb", "29, 158, 117");
                  const colors = [
                    `rgba(${accentRgb}, 1)`,
                    `rgba(${accentRgb}, 0.52)`,
                    "#f59e0b",
                    "#dde8f0",
                  ];
                  return (
                    <>
                      {/* Flash do estouro: brilho radial que expande e some rápido, no instante da explosão */}
                      <div style={{
                        position: "absolute",
                        left: "50%",
                        top: 138,
                        width: 100,
                        height: 100,
                        borderRadius: "50%",
                        background: `radial-gradient(circle, rgba(${accentRgb},0.55) 0%, rgba(${accentRgb},0.16) 45%, rgba(${accentRgb},0) 72%)`,
                        animation: "cf-shock 0.46s cubic-bezier(0.15,0.8,0.4,1) both",
                      }} />
                      {particles.map((p, i) => (
                        <div
                          key={i}
                          style={{
                            position: "absolute",
                            left: "50%",
                            top: 138,
                            width: p.w,
                            height: p.h,
                            borderRadius: p.round,
                            background: colors[p.colorIdx],
                            animation: `cf-burst-arc ${p.duration}s ${p.delay}s both`,
                            willChange: "transform, opacity",
                            "--cf-bx":  `${p.bx}px`,
                            "--cf-by":  `${p.by}px`,
                            "--cf-tx":  `${p.tx}px`,
                            "--cf-rot": `${p.rot}deg`,
                          }}
                        />
                      ))}
                    </>
                  );
                })()}
              </div>
            )}
            <div className={styles.successIcon}><FontAwesomeIcon icon={selected ? getExerciseIcon(selected) : faPersonRunning} /></div>
            <div className={styles.successTitle}>Cardio concluído!</div>

            {/* Stats grid */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10, width: '100%', margin: '16px 0 8px' }}>
              {[
                { label: 'Exercício', value: result.exercise_name },
                { label: 'Duração', value: `${duration} min` },
              ].map(({ label, value }) => (
                <div key={label} style={{
                  background: 'rgba(var(--accent-rgb),0.08)',
                  border: '1px solid rgba(var(--accent-rgb),0.18)',
                  borderRadius: 12,
                  padding: '12px 10px',
                  textAlign: 'center',
                }}>
                  <div style={{ fontSize: 15, fontWeight: 800, color: 'var(--blue)', marginBottom: 4, wordBreak: 'break-word' }}>{value}</div>
                  <div style={{ fontSize: 10, color: 'var(--text-0-40)', fontWeight: 600, letterSpacing: '0.06em', textTransform: 'uppercase' }}>{label}</div>
                </div>
              ))}
            </div>

            {/* Points */}
            <div style={{
              width: '100%',
              textAlign: 'center',
              padding: '10px',
              background: 'rgba(239,159,39,0.08)',
              border: '1px solid rgba(239,159,39,0.2)',
              borderRadius: 12,
              fontSize: 15,
              fontWeight: 700,
              color: 'var(--feedback-warning)',
              marginBottom: 8,
            }}>
              +{result.points_awarded} pts ganhos
            </div>

            {/* Nota inline de desafios que esse cardio nao contou (categoria,
                duracao minima ou geofence) — mesmo padrao do WorkoutSummaryModal,
                nunca bloqueia, so avisa. Ver _checkin_exclusion_reasons em
                user_service.py. */}
            {Array.isArray(result.challenge_notes) && result.challenge_notes.length > 0 && (
              <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 8 }}>
                {result.challenge_notes.map((note) => (
                  <div
                    key={note.challenge_id}
                    style={{
                      display: 'flex', alignItems: 'flex-start', gap: 8,
                      padding: '8px 10px',
                      borderRadius: 8,
                      background: 'rgba(245, 158, 11, 0.10)',
                      border: '1px solid rgba(245, 158, 11, 0.30)',
                      fontSize: 11.5,
                      lineHeight: 1.45,
                      color: 'var(--text-0-70)',
                      textAlign: 'left',
                    }}
                  >
                    <FontAwesomeIcon icon={faTriangleExclamation} style={{ color: '#f59e0b', fontSize: 12, marginTop: 2, flexShrink: 0 }} aria-hidden="true" />
                    <span>
                      <strong style={{ color: 'var(--text-primary)' }}>Não contou para "{note.challenge_title}":</strong>{" "}
                      {(note.reasons || []).join(' · ')}
                    </span>
                  </div>
                ))}
              </div>
            )}

            <button className={styles.btnPrimary} onClick={onClose} style={{ width: '100%' }}>Fechar</button>
          </div>
        ) : (
          <form className={styles.form} onSubmit={handleSubmit}>
            <div className={styles.body} ref={scrollRef} onScroll={check}>
              {loading ? (
                <div className={styles.loadingMsg}>Carregando exercícios...</div>
              ) : items.length === 0 ? (
                <div className={styles.emptyMsg}>Nenhum exercício de cardio cadastrado ainda.</div>
              ) : (
                <div className={styles.exerciseGrid}>
                  {items.map(item => (
                    <button
                      key={item.id}
                      type="button"
                      className={`${styles.exerciseCard} ${selected?.id === item.id ? styles.exerciseCardActive : ""}`}
                      onClick={() => setSelected(item)}
                    >
                      <span className={styles.exerciseIcon}><FontAwesomeIcon icon={getExerciseIcon(item)} /></span>
                      <span className={styles.exerciseName}>{item.name}</span>
                      {item.muscle_group && <span className={styles.exerciseMeta}>{item.muscle_group}</span>}
                    </button>
                  ))}
                </div>
              )}
              {canScrollMore && <div className={styles.scrollFade} aria-hidden="true" />}
            </div>

            <div className={styles.durationPanel}>
              <div className={styles.durationSection}>
                <label className={styles.durationLabel}>Duração</label>
                <div className={styles.durationRow}>
                  {DURATION_OPTIONS.map(d => (
                    <button
                      key={d}
                      type="button"
                      className={`${styles.durationBtn} ${duration === d ? styles.durationBtnActive : ""}`}
                      onClick={() => setDuration(d)}
                    >
                      {d} min
                    </button>
                  ))}
                  <div className={styles.durationCustom}>
                    <input
                      type="number"
                      min={1}
                      max={300}
                      value={duration ?? ""}
                      onChange={e => setDuration(e.target.value === "" ? null : Number(e.target.value))}
                      className={styles.durationInput}
                    />
                    <span className={styles.durationUnit}>min</span>
                  </div>
                </div>
              </div>

              {photoRequired && (
                <div className={styles.durationSection}>
                  <label className={styles.durationLabel} htmlFor="cardio-photo">
                    Foto (obrigatória - desafio com registro fotográfico)
                  </label>
                  <input
                    id="cardio-photo"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={(e) => setPhoto(e.target.files?.[0] || null)}
                    className={styles.durationInput}
                    style={{ width: "100%" }}
                  />
                  {photo && (
                    <span style={{ fontSize: 12, color: "var(--text-0-60)", marginTop: 4, display: "block" }}>
                      {photo.name}
                    </span>
                  )}
                </div>
              )}

              {error && <div className={styles.error}>{error}</div>}
            </div>

            <div className={styles.footer}>
              <button type="button" className={styles.btnSecondary} onClick={onClose} disabled={saving}>
                Cancelar
              </button>
              <button type="submit" className={styles.btnPrimary} disabled={saving || !selected || !duration || loading || (photoRequired && !photo)}>
                {saving ? "Registrando..." : "Registrar"}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
    <ConfirmDialog
      open={confirmState.open}
      title={confirmState.title}
      message={confirmState.message}
      confirmLabel={confirmState.confirmLabel}
      cancelLabel={confirmState.cancelLabel}
      confirmIcon={confirmState.confirmIcon}
      cancelIcon={confirmState.cancelIcon}
      icon={confirmState.icon}
      confirmVariant={confirmState.confirmVariant}
      onDismiss={closeConfirm}
      onClose={closeConfirm}
      onConfirm={runConfirm}
      loading={saving}
    />
    </>
  );
}
